/** @file Projects browser data: loads GET /projects (latest wins), creates, trashes, restores, purges. */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ProjectListItem } from '../../api/apiTypes';
import type { ApiClient } from '../../api/createApiClient';
import type { ProjectsApi } from '../../api/createProjectsApi';
import { navigate } from '../../shell/router/navigate';
import { projectPath } from '../../shell/router/projectPath';

/** State and actions for ProjectsScreen; `failure` keeps the last thrown value for errorText, `busy` is a create or trash in flight. */
export function useProjects(client: Pick<ApiClient, 'listProjects' | 'createProject' | 'trashProject'>, projects: Pick<ProjectsApi, 'restore' | 'purge'>) {
  const [items, setItems] = useState<ProjectListItem[] | null>(null);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const latest = useRef(0);
  const inflight = useRef(false);
  const fail = useCallback((thrown: unknown) => { setFailure({ thrown }); }, []);
  const reload = useCallback(() => {
    latest.current += 1;
    const request = latest.current;
    const current = (): boolean => request === latest.current;
    setLoading(true);
    client.listProjects()
      .then((r) => { if (current()) { setItems(r.projects); setFailure(null); } }, (thrown: unknown) => { if (current()) fail(thrown); })
      .finally(() => { if (current()) setLoading(false); });
  }, [client, fail]);
  useEffect(reload, [reload]);
  const mutate = (start: () => Promise<void>): void => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy(true);
    start().catch(fail).finally(() => { inflight.current = false; setBusy(false); });
  };
  const create = (name: string): void => {
    mutate(async () => { navigate(projectPath((await client.createProject(name)).id)); });
  };
  const trash = (id: string): void => {
    mutate(async () => { await client.trashProject(id); reload(); });
  };
  const restore = (id: string): void => {
    mutate(async () => { await projects.restore(id); reload(); });
  };
  const purge = (id: string): void => {
    mutate(async () => { await projects.purge(id); reload(); });
  };
  return { items, loading, failure, busy, reload, create, trash, restore, purge };
}
